import { folderLabel, type AnalysisReport, type BenchmarkReport, type InventoryReport, type MediaReport, type MediaRun } from "../../../shared/media";

export function formatBytes(n: number): string {
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)} TB`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)} MB`;
  return n > 0 ? `${Math.max(1, Math.round(n / 1e3))} KB` : "0 KB";
}

export function formatDuration(seconds: number): string {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))} s`;
  const m = Math.round(seconds / 60);
  if (m < 90) return `${m} min`;
  const h = seconds / 3600;
  if (h < 48) return `${h.toFixed(h < 10 ? 1 : 0)} h`;
  return `${(h / 24).toFixed(1)} days`;
}

const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : "—");
const n = (x: number) => x.toLocaleString();

const DATE_SOURCE_LABEL: Record<string, string> = {
  exif: "Camera date (EXIF)",
  xmp: "Edited date (XMP)",
  video: "Video date",
  filename: "From the file name",
  folder: "Year from the folder name",
  none: "No date found",
};

export function MediaReportView({ run, report }: { run: MediaRun; report: MediaReport }) {
  return (
    <section className="media-report">
      <h4>
        {report.kind === "inventory" ? "Library scan report" : report.kind === "benchmark" ? "Speed test report" : report.scope === "sample" ? "Sample analysis report" : report.update ? "New photos report" : "Analysis report"}{" "}
        <span className="muted small">· {new Date(report.generatedAt).toLocaleString()}</span>
      </h4>
      {run.status !== "completed" && <p className="muted small">This run didn't finish, so the report covers only what it got through.</p>}
      {report.kind === "inventory" ? <InventoryView r={report} /> : report.kind === "benchmark" ? <BenchmarkView r={report} /> : <AnalysisView r={report} />}
    </section>
  );
}

function InventoryView({ r }: { r: InventoryReport }) {
  const t = r.totals;
  const media = t.photos + t.videos;
  const undated = r.dateSources.none ?? 0;
  const maxYear = Math.max(1, ...r.years.map((y) => y.files));
  return (
    <>
      <div className="media-tiles">
        <Tile label="Photos" value={n(t.photos)} />
        <Tile label="Videos" value={n(t.videos)} />
        <Tile label="Total size" value={formatBytes(t.bytes)} />
        <Tile label="Have a date" value={pct(media - undated, media)} />
        <Tile label="Have GPS" value={pct(r.withGps, media)} />
        <Tile label="Date conflicts" value={n(r.dateConflicts)} />
      </div>
      <p className="muted small">
        {n(t.files)} files in all: {n(t.sidecars)} sidecar files and {n(t.other)} other files. {t.excludedFolders ? `${n(t.excludedFolders)} folders skipped. ` : ""}
        Took {Object.values(r.durations).reduce((a, b) => a + b, 0) > 0 ? formatDuration(Object.values(r.durations).reduce((a, b) => a + b, 0)) : "—"}.
      </p>

      <h5>Where dates come from</h5>
      <ul className="media-bars">
        {Object.entries(r.dateSources)
          .sort((a, b) => b[1] - a[1])
          .map(([k, v]) => (
            <li key={k}>
              <span>{DATE_SOURCE_LABEL[k] ?? k}</span>
              <span className="media-barline">
                <span style={{ width: pct(v, media) }} className={k === "none" ? "warn" : ""} />
              </span>
              <span className="muted">{n(v)}</span>
            </li>
          ))}
      </ul>

      {r.years.length > 0 && (
        <>
          <h5>Photos and videos by year</h5>
          <div className="media-years" role="img" aria-label="Files per year">
            {r.years.map((y) => (
              <div key={y.year} title={`${y.year}: ${n(y.files)}`}>
                <span style={{ height: `${Math.max(2, (y.files / maxYear) * 100)}%` }} />
                <small>{String(y.year).slice(2)}</small>
              </div>
            ))}
          </div>
        </>
      )}

      <h5>By folder</h5>
      <div className="media-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Folder</th>
              <th className="num">Photos</th>
              <th className="num">Videos</th>
              <th className="num">Size</th>
              <th className="num">No date</th>
              <th className="num">Conflicts</th>
              <th>Dates</th>
            </tr>
          </thead>
          <tbody>
            {r.byFolder.map((f) => (
              <tr key={f.folder}>
                <td title={f.folder}>{folderLabel(f.folder)}</td>
                <td className="num">{n(f.photos)}</td>
                <td className="num">{n(f.videos)}</td>
                <td className="num">{formatBytes(f.bytes)}</td>
                <td className={`num ${f.missingDate ? "warn-text" : ""}`}>{n(f.missingDate)}</td>
                <td className={`num ${f.dateConflicts ? "warn-text" : ""}`}>{n(f.dateConflicts)}</td>
                <td className="muted">{f.earliest ? `${f.earliest.slice(0, 4)}–${f.latest?.slice(0, 4)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="media-cols">
        <div>
          <h5>Formats</h5>
          <table className="table">
            <tbody>
              {r.byFormat.slice(0, 14).map((f) => (
                <tr key={f.ext}>
                  <td>{f.ext || "(none)"}</td>
                  <td className="muted">{f.kind}</td>
                  <td className="num">{n(f.files)}</td>
                  <td className="num">{formatBytes(f.bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h5>Cameras</h5>
          <table className="table">
            <tbody>
              {r.cameras.slice(0, 14).map((c) => (
                <tr key={c.model}>
                  <td>{c.model}</td>
                  <td className="num">{n(c.files)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(r.issues.unreadable > 0 || r.issues.zeroByte > 0 || r.issues.metadataFailed > 0) && (
        <>
          <h5>Problems</h5>
          <p className="muted small">
            {n(r.issues.unreadable)} unreadable · {n(r.issues.zeroByte)} empty files · {n(r.issues.metadataFailed)} whose details couldn't be read
          </p>
          <ul className="media-examples">
            {r.examples.map((e, i) => (
              <li key={i}>
                <span className="muted">{e.reason}</span> {e.path}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function BenchmarkView({ r }: { r: BenchmarkReport }) {
  const total = r.projections.reduce((a, p) => a + p.hours, 0);
  return (
    <>
      <div className="media-tiles">
        <Tile label="Sample" value={`${n(r.sample.files)} files`} />
        <Tile label="Sample size" value={formatBytes(r.sample.bytes)} />
        <Tile label="Network read" value={`${r.network.readMbPerSec.toFixed(1)} MB/s`} />
        <Tile label="Library" value={`${n(r.library.photos + r.library.videos)} files`} />
        <Tile label="Estimated full analysis" value={formatDuration(total * 3600)} />
      </div>
      <p className="muted small">{r.network.note}</p>

      <h5>Measured on the sample</h5>
      <table className="table">
        <thead>
          <tr>
            <th>Step</th>
            <th className="num">Files</th>
            <th className="num">Per file</th>
            <th className="num">Rate</th>
          </tr>
        </thead>
        <tbody>
          {r.stages.map((s) => (
            <tr key={s.stage}>
              <td>{s.label}</td>
              <td className="num">{n(s.files)}</td>
              <td className="num">{s.files ? `${Math.round((s.seconds / s.files) * 1000)} ms` : "—"}</td>
              <td className="num">{s.bytes && s.seconds ? `${(s.bytes / 1e6 / s.seconds).toFixed(1)} MB/s` : s.seconds ? `${(s.files / s.seconds).toFixed(1)} files/s` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h5>Projected for the whole library</h5>
      <table className="table">
        <tbody>
          {r.projections.map((p) => (
            <tr key={p.stage}>
              <td>{p.label}</td>
              <td className="num">{formatDuration(p.hours * 3600)}</td>
              <td className="muted small">{p.basis}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="muted small">
        {r.assumptions.map((a, i) => (
          <li key={i}>{a}</li>
        ))}
      </ul>
    </>
  );
}

function AnalysisView({ r }: { r: AnalysisReport }) {
  const c = r.counts;
  const total = r.projections.reduce((a, p) => a + p.hours, 0);
  return (
    <>
      {r.update && (
        <p>
          {r.update.newFiles + r.update.changedFiles === 0
            ? "No new or changed photos since the last scan."
            : `${n(r.update.newFiles)} new and ${n(r.update.changedFiles)} changed photos and videos found; ${n(r.update.read)} read and analysed.`}
          {r.update.missingFiles > 0 ? ` ${n(r.update.missingFiles)} files are no longer on the share.` : ""}
        </p>
      )}
      <div className="media-tiles">
        <Tile label="Photos and videos" value={n(c.files)} />
        <Tile label="Descriptions" value={n(c.captions)} />
        <Tile label="Faces" value={n(c.faces)} />
        {r.privacy ? (
          <Tile label="Kept private" value={n(r.privacy.auto + r.privacy.you)} />
        ) : (
          <Tile label="Flagged for a look" value={n(c.flagged)} />
        )}
        <Tile label="Look-alike groups" value={n(c.groups)} />
        {r.scope === "all" && <Tile label="Extra identical copies" value={n(c.extraCopies)} />}
        {r.scope === "sample" && total > 0 && <Tile label="Whole library would take" value={formatDuration(total * 3600)} />}
      </div>
      {r.tags.length > 0 && (
        <>
          <h5>Most common tags</h5>
          <div className="chip-row wrap">
            {r.tags.slice(0, 30).map((t) => (
              <span key={t.tag} className="chip chip-sm">
                {t.tag} <span className="muted">{n(t.files)}</span>
              </span>
            ))}
          </div>
        </>
      )}
      {r.flags.length > 0 && (
        <>
          <h5>{r.privacy ? "Flagged, so kept private" : "Flagged for a look"}</h5>
          <p className="muted small">
            {r.flags.map((f) => `${f.category} ${n(f.files)}`).join(" · ")}.{" "}
            {r.privacy
              ? "Flagged photos, and photos that look very like them, are kept private automatically; you only need to look if you want to rescue one. Nothing is hidden from you, moved or deleted."
              : "These only queue photos for you to check; nothing is hidden or deleted."}
          </p>
        </>
      )}
      {r.privacy && (
        <>
          <h5>Private and the wall</h5>
          <p className="muted small">
            {n(r.privacy.auto)} kept private automatically · {n(r.privacy.you)} by you · {n(r.privacy.fine)} you let out · {r.privacy.foldersApproved} of {r.privacy.folders}{" "}
            folders approved for the wall · {n(r.privacy.wallEligible)} photos could go on it
            {r.privacy.skippedVideos ? ` · ${n(r.privacy.skippedVideos)} videos not analysed and kept private` : ""}. Approve folders under Review on the PC.
          </p>
        </>
      )}
      <h5>Time per step</h5>
      <table className="table">
        <tbody>
          {r.stages.map((s) => (
            <tr key={s.stage}>
              <td>{s.label}</td>
              <td className="num">{n(s.files)}</td>
              <td className="num">{s.files ? `${Math.round((s.seconds / s.files) * 1000)} ms each` : "—"}</td>
              <td className="num">{formatDuration(s.seconds)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {r.projections.length > 0 && (
        <>
          <h5>Projected for the whole library</h5>
          <table className="table">
            <tbody>
              {r.projections.map((p) => (
                <tr key={p.stage}>
                  <td>{p.label}</td>
                  <td className="num">{formatDuration(p.hours * 3600)}</td>
                  <td className="muted small">{p.basis}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small">Plus reading every file once (see the speed test). Work done for the sample counts toward the full run.</p>
        </>
      )}
      <p className="muted small">Models: {Object.values(r.models).join(" · ")}</p>
    </>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="media-tile">
      <span className="muted small">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
